<template>
  <div>
    <v-row :class="{ 'wide-layout-row': wideLayout }">
      <!-- Fields Section -->
      <v-col :cols="12" :md="wideLayout ? 6 : 12" :class="wideLayout ? 'pr-md-6' : ''">
        <!-- Naziv limita i Period limita -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6">
            <text-field
              :label="$t('limit_name')"
              v-model="limitName"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6">
            <autocomplete
              :label="$t('limit_period')"
              v-model="limitPeriod"
              :items="limitPeriodOptions"
              item-text="text"
              item-value="value"
              dense
            />
          </v-col>
        </v-row>

        <!-- Validnost limita -->
        <div class="mb-4">
          <div class="subtitle-1 font-weight-bold text-uppercase mb-3 black--text">
            {{ $t("limit_validity") }}
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <date-picker
                :label="$t('valid_from')"
                v-model="validFrom"
                dense
                single-date
              />
            </v-col>
            <v-col cols="12" sm="6">
              <date-picker
                :label="$t('valid_until')"
                v-model="validUntil"
                dense
                single-date
                :persistent-hint="true"
                :hint="!validUntil ? $t('valid_unlimited') : ''"
              />
            </v-col>
          </v-row>
        </div>

        <!-- Dynamic inputs -->
        <div v-if="limitPeriod === 'daily' && selectedLimitType === 'money'" class="mb-4">
          <text-field
            :label="$t('money_per_day')"
            type="number"
            dense
            outlined
            v-model="moneyPerDay"
            hide-details
            style="max-width: 200px"
            :suffix="restaurantCurrency"
          />
        </div>

        <div v-if="limitPeriod === 'daily' && selectedLimitType === 'meals'" class="mb-4">
          <text-field
            :label="$t('meals_per_day')"
            type="number"
            dense
            outlined
            v-model="mealsPerDay"
            hide-details
            style="max-width: 200px"
          />
        </div>

        <div
          v-if="limitPeriod === 'per_shift' && selectedLimitType === 'money'"
          class="mb-4"
        >
          <div class="subtitle-1 font-weight-bold text-uppercase mb-3 black--text">
            {{ $t("money_per_shift") }}
          </div>
          <v-row>
            <v-col cols="12" sm="6" md="3" v-for="index in 4" :key="index">
              <text-field
                :label="`${$t('shift_' + index)}`"
                type="number"
                dense
                outlined
                v-model="moneyPerShift[index - 1]"
                hide-details
                :suffix="restaurantCurrency"
              />
            </v-col>
          </v-row>
        </div>

        <div
          v-if="limitPeriod === 'per_shift' && selectedLimitType === 'meals'"
          class="mb-4"
        >
          <div class="subtitle-1 font-weight-bold text-uppercase mb-3 black--text">
            {{ $t("meals_per_shift") }}
          </div>
          <v-row>
            <v-col cols="12" sm="6" md="3" v-for="index in 4" :key="index">
              <text-field
                :label="`${$t('shift_' + index)}`"
                type="number"
                dense
                outlined
                v-model="mealsPerShift[index - 1]"
                hide-details
              />
            </v-col>
          </v-row>
        </div>

        <!-- Checkboxes -->
        <v-checkbox
          v-if="restaurant?.voucher"
          @change="changeCanOrderExtra"
          v-model="restaurant.voucher.params.limit.can_order_extra"
          :label="$t('worker_extra_payment')"
          hide-details
          dense
          color="deep-orange"
        ></v-checkbox>
        <v-checkbox
          v-else-if="!value && !restaurant?.voucher"
          @change="changeCanOrderExtra"
          v-model="internalCanOrderExtra"
          :label="$t('worker_can_order_above_limit')"
          hide-details
          dense
          color="deep-orange"
        ></v-checkbox>
      </v-col>

      <!-- Cards Section -->
      <v-col :cols="12" :md="wideLayout ? 6 : 12" :class="wideLayout ? 'pl-md-6' : ''">
        <div class="mb-4">
          <div class="subtitle-1 font-weight-bold text-uppercase mb-3 black--text">
            {{ $t("limit_type") }}
          </div>

          <v-alert border="left" color="blue lighten-5" class="mb-4" :icon="false" dense>
            <v-row no-gutters align="center">
              <v-icon color="amber" class="mr-2">mdi-lightbulb-on-outline</v-icon>
              <strong>{{ $t("help") }}</strong>
            </v-row>
            <div class="mt-1">{{ $t("limit_type_help") }}</div>
          </v-alert>

          <v-radio-group v-model="selectedLimitType" hide-details class="mt-0">
            <v-card
              v-for="option in limitTypeOptions"
              :key="option.value"
              class="limit-option-card mb-3"
              outlined
            >
              <v-card-text class="py-3">
                <v-radio :value="option.value" color="deep-orange">
                  <template v-slot:label>
                    <div>
                      <div class="d-flex align-center">
                        <v-icon small class="mr-2" :color="option.iconColor">
                          {{ option.icon }}
                        </v-icon>
                        <span class="font-weight-medium">{{ $t(option.label) }}</span>
                      </div>
                      <div class="option-description">
                        {{ $t(option.description, { currency: restaurantCurrency }) }}
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-card-text>
            </v-card>
          </v-radio-group>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import companyService from "@/services/company";

export default {
  name: "GeneralSettingsTab",
  mixins: [hasSnackbar],
  props: {
    restaurant: { type: Object, default: null },
    company: { type: Object, default: null },
    value: { type: String, default: null },
    localCanOrderExtra: { type: Boolean, default: false },
    wideLayout: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedLimitType: "money",
      limitName: "",
      limitPeriod: null,
      validFrom: new Date().toISOString().substr(0, 10),
      validUntil: null,
      moneyPerDay: null,
      moneyPerShift: [null, null, null, null],
      mealsPerDay: null,
      mealsPerShift: [null, null, null, null],
      internalCanOrderExtra: this.localCanOrderExtra,
      limitPeriodOptions: [
        { value: "daily", text: this.$t("per_day") },
        { value: "weekly", text: this.$t("per_week") },
        { value: "monthly", text: this.$t("per_month") },
        { value: "per_shift", text: this.$t("per_shift") },
      ],
      limitTypeOptions: [
        {
          value: "money",
          icon: "mdi-currency-eur",
          iconColor: "orange darken-2",
          label: "money_limit",
          description: "money_limit_description",
        },
        {
          value: "meals",
          icon: "mdi-food",
          iconColor: "grey darken-1",
          label: "meal_count_limit",
          description: "meal_count_limit_description",
        },
        {
          value: "categories",
          icon: "mdi-folder-multiple",
          iconColor: "amber darken-2",
          label: "by_food_categories",
          description: "by_food_categories_description",
        },
        {
          value: "vouchers",
          icon: "mdi-ticket",
          iconColor: "cyan darken-1",
          label: "voucher_count_limit",
          description: "voucher_count_limit_description",
        },
        {
          value: "no_limit",
          icon: "mdi-infinity",
          iconColor: "green darken-1",
          label: "no_limit",
          description: "no_limit_description",
        },
      ],
    };
  },
  watch: {
    localCanOrderExtra(val) {
      this.internalCanOrderExtra = val;
    },
  },
  methods: {
    getCanOrderExtra() {
      return (
        this.internalCanOrderExtra ||
        this.restaurant?.voucher?.params?.limit?.can_order_extra ||
        false
      );
    },
    changeCanOrderExtra() {
      if (!this.restaurant) return;
      const canOrderExtraValue =
        this.restaurant?.voucher?.params?.limit?.can_order_extra ??
        this.internalCanOrderExtra;

      companyService
        .setCompanyCanOrderExtra({
          vaucer: this.restaurant,
          can_order_extra: canOrderExtraValue,
          company: this.company,
        })
        .then(({ data }) => {
          this.$emit("update-voucher", data);
          this.showSuccessSnackbar(this.$t("order_extra_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_updating_order_extra"));
        });
    },
  },
};
</script>

<style scoped>
.limit-option-card {
  transition: all 0.2s ease;
}

.limit-option-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.option-description {
  font-size: 0.813rem;
  color: #757575;
  line-height: 1.4;
}
</style>
